import React from 'react';
import { Loader2, Trophy, XCircle, Star } from 'lucide-react';
import { pickLocalizedText } from '../../../utils/localizedContent';

export default function CompetitionShell({
  title,
  isAdmin,
  eventStatus,
  bracket,
  actionLoading,
  onFinalizeTournament,
  onCancelTournament,
  onDeleteTournament,
  participants,
  lang,
  children,
}) {
  const phase = bracket?.phase || (bracket ? 'active' : 'registration');
  const text = (es, en) => pickLocalizedText(lang, es, en);
  const champion = bracket?.status === 'completed' ? bracket?.standings?.[0]?.player : null;
  const championData = champion?.user_id
    ? (participants || []).find((p) => p.user_id === champion.user_id)
    : null;
  const championName = championData?.name || champion?.name;
  const championAvatar = championData?.avatar;
  const championRankingPoints = championData?.ranking_points;
  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <h3 className="text-foreground font-bold mb-4 flex items-center gap-2">
        <Trophy size={20} className="text-primary" />
        {title}
      </h3>
      {championName && (
        <div className="mb-4 rounded-2xl border border-primary/35 bg-gradient-to-r from-primary/15 to-primary/5 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-full overflow-hidden border border-primary/40 bg-[#111827] flex items-center justify-center text-primary font-bold">
                {championAvatar ? (
                  <img src={championAvatar} alt={championName} className="w-full h-full object-cover" />
                ) : (
                  (championName || '?').charAt(0).toUpperCase()
                )}
              </div>
              <div className="min-w-0">
                <p className="text-primary text-[11px] uppercase tracking-wide font-semibold">
                  {text('Ganador del torneo', 'Tournament winner')}
                </p>
                <p className="text-foreground font-bold truncate">{championName}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide">
                {text('Puntos ranking', 'Ranking points')}
              </p>
              <p className="text-primary font-bold text-lg">
                {Number(championRankingPoints || 0)} pts
              </p>
            </div>
          </div>
        </div>
      )}
      <CompetitionTopbar
        isAdmin={isAdmin}
        eventStatus={eventStatus}
        bracket={bracket}
        actionLoading={actionLoading}
        onFinalizeTournament={onFinalizeTournament}
        onCancelTournament={onCancelTournament}
        onDeleteTournament={onDeleteTournament}
        lang={lang}
      />
      <div className="mb-4 text-xs text-muted-foreground">
        {text('Fase actual', 'Current phase')}: <span className="text-primary font-semibold">{phase}</span>
      </div>
      <ParticipantsGrid participants={participants} lang={lang} />
      {children}
    </div>
  );
}

function CompetitionTopbar({ isAdmin, eventStatus, bracket, actionLoading, onFinalizeTournament, onCancelTournament, onDeleteTournament, lang }) {
  if (!isAdmin) return null;
  const canFinalize = !!bracket && bracket.status === 'ready_to_finalize';
  const canDelete = (eventStatus === 'upcoming') || (!!bracket && bracket.status === 'completed');
  const text = (es, en) => pickLocalizedText(lang, es, en);
  return (
    <div className="mb-5 flex items-center justify-end gap-2">
      {canFinalize && (
        <button
          onClick={onFinalizeTournament}
          disabled={actionLoading === 'finalize-tournament'}
          className="px-4 py-2 rounded-xl border border-[#10B981]/35 bg-[#10B981]/10 text-[#86EFAC] text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
        >
          {actionLoading === 'finalize-tournament' ? <Loader2 size={15} className="animate-spin" /> : <Trophy size={15} />}
          {text('Finalizar torneo', 'Finalize tournament')}
        </button>
      )}
      {canDelete ? (
        <button
          onClick={onDeleteTournament}
          disabled={actionLoading === 'delete-tournament'}
          className="px-4 py-2 rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/10 text-[#FCA5A5] text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
        >
          {actionLoading === 'delete-tournament' ? <Loader2 size={15} className="animate-spin" /> : <XCircle size={15} />}
          {text('Eliminar torneo', 'Delete tournament')}
        </button>
      ) : (
        <button
          onClick={onCancelTournament}
          disabled={actionLoading === 'cancel-tournament'}
          className="px-4 py-2 rounded-xl border border-[#C45B4A]/35 bg-[#C45B4A]/10 text-[#FCA5A5] text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
        >
          {actionLoading === 'cancel-tournament' ? <Loader2 size={15} className="animate-spin" /> : <XCircle size={15} />}
          {text('Cancelar torneo', 'Cancel tournament')}
        </button>
      )}
    </div>
  );
}

function ParticipantsGrid({ participants, lang }) {
  if (!participants?.length) return null;
  const text = (es, en) => pickLocalizedText(lang, es, en);
  return (
    <div className="mb-6">
      <p className="text-primary text-xs uppercase tracking-wide mb-3">
        {text('Participantes desplegados', 'Deployed participants')}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {participants.map((p) => (
          <div key={p.user_id} className="rounded-xl border border-border bg-card p-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#C9A962]/20 border border-[#C9A962]/30 flex items-center justify-center text-[#C9A962] font-bold">
                {p.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="min-w-0">
                <p className="text-foreground text-sm font-semibold truncate">#{p.seed || '-'} {p.name || 'Unknown'}</p>
                <p className="text-muted-foreground text-xs truncate">{p.email || p.user_id}</p>
              </div>
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-primary/10 border border-primary/25 text-primary">
              <Star size={12} />
              {text('Ranking', 'Ranking')}: {p.ranking_points || 0} pts
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
